import {
    Alert,
    Divider,
    Flex,
    Heading,
    ListItem,
    Stack,
    Text,
    ThemeColorVariable,
    ThemeFontSizeVariable,
    ThemeSpaceVariable,
} from "@caspeco/casper-ui-library";
import Price from "components/price";
import PreorderGroupWrapper from "components/selection-components/preorder/preorder-group-wrapper";
import validateMenuGroup, {
    ValidationContext,
    ValidationResult,
} from "components/selection-components/preorder/validation/menu-validators";
import RuleExplaination from "components/selection-components/preorder/widgets/rule-explaination";
import {
    createWidget,
    WidgetContext,
} from "components/selection-components/preorder/widgets/widget-factory";
import ShowMoreText from "components/show-more-text/show-more-text";
import { useBookingStateContext } from "hooks/use-booking-state";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArticleStock } from "types/article-stock";
import { MenuGroup, MenuGroupType } from "types/menu-group";
import { MenuItemWebFlags } from "types/menu-item";

interface IPreOrderMenuGroup {
    menuGroup: MenuGroup;
    guests?: number;
    currentGroupQuantities: number[];
    setGroupQuantities: (quantities: number[]) => void;
    onValidationChange?: (isValid: boolean) => void;
    isInfoGroup?: boolean;
    articleStocks?: Map<number, ArticleStock[]>;
}

const PreorderMenuGroup = ({
    menuGroup,
    guests,
    setGroupQuantities,
    currentGroupQuantities,
    onValidationChange,
    isInfoGroup,
    articleStocks,
}: IPreOrderMenuGroup) => {
    const { t } = useTranslation();
    const { isActivityBooking } = useBookingStateContext();
    const addItemButtonText = t("preorder.addItemButtonText");

    const totalGroupQuantities = currentGroupQuantities.reduce(
        (acc, quantity) => acc + quantity,
        0
    );

    // These will benefit from having inline errors
    const hasWidgetInlineError = (groupType: MenuGroupType) =>
        groupType === MenuGroupType.LimitedToGuests ||
        groupType === MenuGroupType.LimitedToGuestsAndChoices ||
        groupType === MenuGroupType.AmountChoice;

    const [validationResult, setValidationResult] = useState<ValidationResult>({
        isValid: true,
        messageKey: "",
    });

    const [isValid, setIsValid] = useState(true);

    useEffect(() => {
        const validationContext: ValidationContext = {
            menuGroup,
            guests: guests || 1,
            totalGroupQuantities,
            allQuantities: currentGroupQuantities,
            optional: menuGroup.optional,
            articleStocks,
        };

        const result = validateMenuGroup(menuGroup.groupType, validationContext);
        setValidationResult(result);

        setIsValid(result.isValid);
    }, [menuGroup, guests, totalGroupQuantities, currentGroupQuantities, articleStocks, t]);

    useEffect(() => {
        onValidationChange?.(isValid);
    }, [isValid, onValidationChange]);

    useEffect(() => {
        if (menuGroup.groupType !== MenuGroupType.IncludedPerGuest) return;
        const guestCount = guests || 1;
        const expectedQuantities = (menuGroup.items ?? []).map(() => guestCount);
        const needsUpdate = expectedQuantities.some((q, i) => q !== currentGroupQuantities[i]);
        if (needsUpdate) {
            setGroupQuantities(expectedQuantities);
        }
    }, [menuGroup, guests, currentGroupQuantities, setGroupQuantities]);

    const [itemErrorMessages, setItemErrorMessages] = useState<string[]>(
        Array(menuGroup.items?.length ?? 0).fill("")
    );

    const handleItemErrorChange = (idx: number, message: string) => {
        setItemErrorMessages((prev) => prev.map((msg, i) => (i === idx ? message : msg)));
    };

    const handleQuantityChange = (idx: number, newValue: number, resetMessage: boolean) => {
        const newQuantities = currentGroupQuantities.toSpliced(idx, 1, newValue);
        setGroupQuantities(newQuantities);

        if (itemErrorMessages[idx] && resetMessage) {
            handleItemErrorChange(idx, "");
        }
    };

    const setQuantitySingelChoice = (idx: number) => {
        const newQuantities = currentGroupQuantities.map((_, i) => (i === idx ? 1 : 0));
        setGroupQuantities(newQuantities);

        if (itemErrorMessages[idx]) {
            handleItemErrorChange(idx, "");
        }
    };

    return (
        <>
            <RuleExplaination
                validationResult={validationResult}
                type={!validationResult.isValid ? "warning" : "info"}
            />
            <PreorderGroupWrapper menuGroup={menuGroup}>
                {menuGroup.items?.map((menuItem, idx) => {
                    const setQuantity = (newValue: number, resetMessage: boolean) =>
                        handleQuantityChange(idx, newValue, resetMessage);

                    if (menuItem.webFlags === MenuItemWebFlags.Hide) return null;

                    const showPrice = menuItem.webFlags === MenuItemWebFlags.TextPriceAndQuantity;
                    const showWidget = menuItem.webFlags !== MenuItemWebFlags.TextOnly;

                    const widgetContext: WidgetContext = {
                        menuGroup,
                        guests,
                        totalGroupQuantities,
                        currentQuantity: currentGroupQuantities[idx],
                        allQuantities: currentGroupQuantities,
                        setQuantity,
                        setQuantitySingelChoice: () => setQuantitySingelChoice(idx),
                        onErrorChange: (_, message) => handleItemErrorChange(idx, message),
                        addItemButtonText,
                        itemFlag: menuItem.webFlags,
                        t,
                        articleId: menuItem.articleID,
                        articleStocks,
                    };

                    return (
                        <div key={`menugroup-${idx}`}>
                            <ListItem
                                display="flex"
                                flexDirection="column"
                                justifyContent="space-between"
                                gap={ThemeSpaceVariable.None}
                                margin={`${ThemeSpaceVariable.Medium} 0`}
                                minH={ThemeSpaceVariable.X2Large}
                            >
                                <Heading
                                    as="h3"
                                    fontSize={ThemeFontSizeVariable.Large}
                                    fontWeight="500"
                                >
                                    {menuItem.name}
                                </Heading>
                                <Flex
                                    justify="space-between"
                                    align="start"
                                    gap={ThemeSpaceVariable.Medium}
                                >
                                    <Stack spacing={ThemeSpaceVariable.None}>
                                        <ShowMoreText
                                            text={menuItem.description}
                                            initialLines={!isInfoGroup ? 2 : undefined}
                                            textProps={{ fontWeight: "400" }}
                                            backgroundColor={
                                                isActivityBooking
                                                    ? ThemeColorVariable.Surface
                                                    : ThemeColorVariable.Background
                                            }
                                        />

                                        {showPrice && (
                                            <Text fontWeight="500">
                                                <Price amount={menuItem.price} display="symbol" />
                                            </Text>
                                        )}
                                    </Stack>
                                    {showWidget &&
                                        createWidget(menuGroup.groupType, widgetContext, idx)}
                                </Flex>
                            </ListItem>
                            {hasWidgetInlineError(menuGroup.groupType) &&
                                itemErrorMessages[idx] && (
                                    <Alert
                                        type={
                                            menuGroup.groupType === MenuGroupType.AmountChoice
                                                ? "info"
                                                : "warning"
                                        }
                                        mb={ThemeSpaceVariable.Medium}
                                    >
                                        {itemErrorMessages[idx]}
                                    </Alert>
                                )}
                            <Divider backgroundColor={ThemeColorVariable.OnBackgroundBorder} />
                        </div>
                    );
                })}
            </PreorderGroupWrapper>
        </>
    );
};

export default PreorderMenuGroup;
